import { useCallback, useEffect, useState } from 'react'

import type { QueryObserverResult } from '@tanstack/react-query'
import { AxiosError } from 'axios'
import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import { ClienteFormMapper } from '@/features/proposta/brasil/domain/Comprador/use-case/cliente-form-mapper'
import { MaePaiUseCase } from '@/features/proposta/brasil/domain/Comprador/use-case/mae-pai-use-case'
import { validarCpfComprador } from '@/features/proposta/brasil/domain/Comprador/validator/cpf-comprador-validator'
import type { CompradorPessoaFisicaStepSchemaInput } from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pf.schema'
import type { TCompradoresCadastrados } from '@/features/proposta/brasil/types/proposta.type'
import { showToast } from '@/infra/sonner'
import type { TCliente } from '@/types/cliente'

type TDadosComprador = {
  onNext: () => void
  buscarPorId: () => Promise<QueryObserverResult<TCliente, AxiosError>>
  buscarPorCpf: () => Promise<QueryObserverResult<TCliente, AxiosError>>
  buscarClienteAtendimentoPorCpf: () => Promise<QueryObserverResult<TCliente, AxiosError>>
  nacionalidadePrincipalId: string
  clienteDoAtendimento: { id: string; cpf?: string | null }
  compradores: TCompradoresCadastrados
  emailsJaVinculados: Record<string, string>
  conjuges: TCompradoresCadastrados[number]['vinculos']
}

export function useDadosCompradorPf({
  onNext,
  buscarPorCpf,
  buscarPorId,
  buscarClienteAtendimentoPorCpf,
  nacionalidadePrincipalId,
  clienteDoAtendimento,
  compradores,
  emailsJaVinculados,
  conjuges,
}: TDadosComprador) {
  const { trigger, setValue, reset, watch, getValues, setError } =
    useFormContext<CompradorPessoaFisicaStepSchemaInput>()
  const { buscouCpf, cpf, id: compradorId } = watch('dados')
  const cpfRaw = cpf?.replaceAll(/\D/g, '')

  const useCase = new ClienteFormMapper(clienteDoAtendimento, emailsJaVinculados, nacionalidadePrincipalId)
  const [modalOpen, setModalOpen] = useState(false)

  const validateOnNext = useCallback(async () => {
    const { ok, error } = useCase.validarAvanco(cpfRaw, !!buscouCpf)

    if (!ok && error === 'MISSING_CPF') {
      trigger('dados.cpf')
      return
    }

    if (!ok && error === 'NEED_SEARCH') {
      showToast({
        variant: 'error',
        message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.busca_obrigatoria'),
      })
      return
    }

    if (await trigger('dados')) onNext()
  }, [cpf, buscouCpf, trigger, onNext])

  const handleBuscarCliente = useCallback(async () => {
    const validacaoCpf = validarCpfComprador(cpfRaw, compradores, conjuges)
    if (validacaoCpf.type === 'ERROR') {
      if (validacaoCpf.reason === 'IS_ALREADY_CONJUGE') {
        setError('dados.cpf', {
          message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.cpf_conjuge_vinculado'),
        })
        return
      }
      showToast({
        variant: 'error',
        message: t('proposta:cadastro_compradores.steps.dados_comprador.erro.cpf_comprador_existente'),
      })
      return
    }
    const result = await buscarPorCpf()
    const fetchResult = useCase.interpretarResultadoBuscaManual(result, compradorId)
    switch (fetchResult.type) {
      case 'SUCCESS':
        showToast({
          variant: 'success',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.sucesso'),
        })
        reset(fetchResult.patch)
        setModalOpen(false)
        break
      case 'NOT_FOUND':
        if (fetchResult.shouldOpenModal) {
          setModalOpen(true)
          return
        }
        showToast({
          variant: 'info',
          message: t('proposta:cadastro_compradores.steps.dados_comprador.busca.nao_encontrado'),
        })
        setValue('dados.buscouCpf', true)
        break
      case 'ERROR':
        showToast({ variant: 'error', message: t('common:errors_gerais.padrao') })
        break
    }
  }, [cpfRaw, compradorId])

  const handleLimpar = useCallback(() => {
    reset(useCase.limparFormulario())
    setModalOpen(false)
  }, [reset])

  const handleConfirmVinculo = useCallback(() => {
    setValue('dados.buscouCpf', true)
    setModalOpen(false)
  }, [setValue])

  const handleMenorIdade = useCallback(async () => {
    const values = getValues()
    const maior18 = values.dados.maiorDe18Anos === 'sim'

    if (!maior18) {
      setValue('conjuge', null)
      setValue('contato', null)
      if (!values.maePai) {
        setValue('maePai', MaePaiUseCase.iniciarDefaultValues(emailsJaVinculados, nacionalidadePrincipalId))
      }
    }
    if (maior18) {
      setValue('maePai', null)
      if (!values.contato) {
        setValue('contato', {
          email: '',
          telefone: {
            origem: 'Brasil',
            numero: '',
            principal: true,
          },
        })
      }
    }
  }, [getValues, nacionalidadePrincipalId, setValue])

  const handleNovoCadastro = useCallback(() => {
    reset({
      mode: 'create',
      tipo: 'PessoaFisica',
      tipoDoCliente: 'Contato',
      dados: {
        email: null,
        id: '',
        cpf,
        nome: '',
        profissaoId: '',
        nacionalidadeId: nacionalidadePrincipalId ?? '',
        maiorDe18Anos: '',
        buscouCpf: true,
      },
      contato: {
        email: '',
        telefone: {
          numero: '',
          origem: undefined,
          principal: true,
        },
      },
      estadoCivil: {
        regimeCasamento: undefined,
        status: undefined,
        tipoAssinatura: undefined,
        uniaoEstavel: undefined,
      },
      emailsJaVinculados,
    })

    setModalOpen(false)
  }, [reset, cpf])

  const handlePreencherDadosDoClienteDoAtendimentoAutomaticamente = async () => {
    if (compradores.length > 0 || buscouCpf) return
    if (!clienteDoAtendimento.id && !clienteDoAtendimento.cpf) return
    const buscarCliente = clienteDoAtendimento.cpf ? buscarClienteAtendimentoPorCpf : buscarPorId
    const resultBuscaCliente = await buscarCliente()

    let clientePorIdNaFalhaCPF: TCliente | undefined
    if (clienteDoAtendimento.cpf && resultBuscaCliente?.error?.status === 404) {
      const result = await buscarPorId()
      clientePorIdNaFalhaCPF = result.data
    }
    const fetchResult = useCase.interpretarResultadoBuscaAutomatica(resultBuscaCliente, clientePorIdNaFalhaCPF)

    switch (fetchResult.type) {
      case 'SUCCESS':
        reset(fetchResult.patch)
        break
      case 'INACTIVE':
        reset(fetchResult.patch)
        break
      case 'ERROR':
        showToast({ variant: 'error', message: 'Não foi possivel preencher cliente do atendimento automaticamente' })
        break
    }
  }

  useEffect(() => {
    handlePreencherDadosDoClienteDoAtendimentoAutomaticamente()
  }, [])

  return {
    modalOpen,
    handlers: {
      validateOnNext,
      handleBuscarCliente,
      handleLimpar,
      setModalOpen,
      handleConfirmVinculo,
      handleNovoCadastro,
      handleMenorIdade,
    },
  }
}
